import { BlockDiagramViewProperties, BlockDiagramViewSettings } from './types';
import { Observable, combineLatest, merge } from 'rxjs';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
  withNamespace,
} from '@ardoq/rxbeach';
import {
  debounceTime,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { APIOrganizationUser, DiffMode, ViewIds } from '@ardoq/api-types';
import { notifyViewLoading } from 'tabview/actions';
import { getGraphComponentLegendProps } from 'tabview/graphComponent/graphComponentLegendUtils';
import { filterInterface } from '@ardoq/filter-interface';
import {
  type HasViewInstanceId,
  RelationshipDiagramViewModelStreamState,
  legacyGetDistinctReferenceKey,
  getGraphNodeCount,
} from '@ardoq/graph';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { returnTrue } from '@ardoq/common-helpers';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import {
  DEFAULT_EXPECTED_DURATION,
  recordViewModelReady,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import {
  cloneGraphItemsAsUpdate,
  getPerformanceTrackingData,
  isSeparateReferencesChange,
} from './utils';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import {
  getComponentIdsFromViewModel,
  getDefaultRelationshipDiagramStateForWorkspaceMode,
} from 'tabview/relationshipDiagrams/utils';
import {
  GlobalRelationshipsViewModelResetStreamShape,
  ViewDataLimitCheck,
} from 'tabview/relationshipDiagrams/viewModel$/types';
import type { ViewDataLimitExceeded } from 'views/environmentals/viewDataLimits';
import { buildViewModel } from 'tabview/relationshipDiagrams/viewModel$/buildViewModel';
import {
  DATA_LIMIT_CHECK_DISABLED,
  buildViewDataLimitCheck,
} from 'tabview/relationshipDiagrams/consts';
import {
  allActiveOrgUsers$,
  globalViewModelResetSource$,
  SharedBlockDiagramsRenderingDataSource,
  sharedBlockDiagramsRenderingDataSource$,
} from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { getFilteredViewSettings$ } from 'views/filteredViewSettings$';
import {
  toggleCollapseGroupReducer,
  expandAllGroupsReducer,
  collapseAllGroupsReducer,
} from 'tabview/relationshipDiagrams/viewModel$/toggleCollapseGroupInWorkspaceMode';
import {
  relationshipDiagramToggleCollapseGroup,
  relationshipDiagramExpandAllGroups,
  relationshipDiagramCollapseAllGroups,
  relationshipDiagramBypassDataLimit,
  type RelationshipDiagramCollapseGroupPayload,
  type ViewIdPayload,
} from 'tabview/relationshipDiagrams/actions';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import { componentsChanged$ } from 'streams/components/components$';
import { handleComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleComponentUpdate';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import type { APIComponentAttributes } from '@ardoq/api-types';
import { getDefaultRelationshipDiagramsViewInterface } from 'tabview/relationshipDiagrams/viewModel$/getDefaultRelationshipDiagramsViewInterface';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { activeDiffMode$ } from 'scope/activeDiffMode$';
import type { ScopeDiffState } from '@ardoq/data-model';
import { i18nLocale$ } from 'streams/i18nLocale$';
import type { I18nLocale } from '@ardoq/i18n';
import { getErrorInfoBoxLabels } from 'locales/getErrorInfoBoxLabels';

export type SourceDataShape =
  RelationshipDiagramViewModelStreamState<BlockDiagramViewSettings> & {
    viewId: ViewIds.BLOCK_DIAGRAM;
    lastViewModelEvent: ViewModelEvent | null;
    dataLimitCheck: ViewDataLimitCheck;
    dataLimitExceeded: ViewDataLimitExceeded | null;
  };

const VIEW_ID = ViewIds.BLOCK_DIAGRAM;

/**
 * Settings that only require animation updates and/or optimized graph rebuild.
 * These change how the graph is displayed but don't change its metamodel structure.
 * Handled by dedicated reducers that mark items for animation.
 */
const ANIMATION_ONLY_SETTINGS: (keyof BlockDiagramViewSettings)[] = [
  'separateReferences',
  'collapsedGroupIds',
];

/**
 * Settings that are purely UI-only and don't affect the view model at all.
 * These can be updated without any graph processing or animations.
 */
const UI_ONLY_SETTINGS: (keyof BlockDiagramViewSettings)[] = [
  viewSettingsConsts.IS_LEGEND_ACTIVE,
];

/**
 * Combined list of settings that should NOT trigger sourceDataResetReducer.
 * Every other view settings change will trigger a full graph rebuild.
 */
const IGNORE_FOR_SOURCE_DATA_REBUILD: (keyof BlockDiagramViewSettings)[] = [
  ...ANIMATION_ONLY_SETTINGS,
  ...UI_ONLY_SETTINGS,
];

// #region EVENT CLASSIFICATION - Document what triggers what

enum ViewModelEventType {
  SOURCE_DATA_RESET = 'SOURCE_DATA_RESET',
  COMPONENT_DATA_UPDATED = 'COMPONENT_DATA_UPDATED',
  SEPARATE_REFERENCES_CHANGED = 'SEPARATE_REFERENCES_CHANGED',
  UI_SETTINGS_CHANGED = 'UI_SETTINGS_CHANGED',
  GROUP_TOGGLED = 'GROUP_TOGGLED',
  ALL_GROUPS_EXPANDED = 'ALL_GROUPS_EXPANDED',
  ALL_GROUPS_COLLAPSED = 'ALL_GROUPS_COLLAPSED',
}

/**
 * Events that trigger full graph rebuild
 */
type GraphRebuildEvent = {
  type: ViewModelEventType.SOURCE_DATA_RESET;
  payload: GlobalRelationshipsViewModelResetStreamShape;
  dataLimitCheck: ViewDataLimitCheck;
};

/**
 * Events that only update components
 */
type ComponentsUpdateEvent = {
  type: ViewModelEventType.COMPONENT_DATA_UPDATED;
  payload: APIComponentAttributes[];
};

/**
 * Events that mark items for animation without structural change
 */
type AnimationOnlyEvent = {
  type: ViewModelEventType.SEPARATE_REFERENCES_CHANGED;
  payload: WorkspaceModeInstanceViewSettings<ViewIds.BLOCK_DIAGRAM>;
};

/**
 * Events that only update UI settings without affecting view model
 */
type UiOnlyEvent = {
  type: ViewModelEventType.UI_SETTINGS_CHANGED;
  payload: WorkspaceModeInstanceViewSettings<ViewIds.BLOCK_DIAGRAM>;
};

/**
 * Events that rebuild graph with smart animation tracking
 */
type GroupCollapseEvent =
  | {
      type: ViewModelEventType.GROUP_TOGGLED;
      payload: RelationshipDiagramCollapseGroupPayload;
    }
  | { type: ViewModelEventType.ALL_GROUPS_EXPANDED; payload: ViewIdPayload }
  | { type: ViewModelEventType.ALL_GROUPS_COLLAPSED; payload: ViewIdPayload };

type ViewModelEvent =
  | GraphRebuildEvent
  | ComponentsUpdateEvent
  | AnimationOnlyEvent
  | UiOnlyEvent
  | GroupCollapseEvent;

// #endregion EVENT CLASSIFICATION

export const resetViewModel = (
  state: SourceDataShape,
  payload: GlobalRelationshipsViewModelResetStreamShape,
  dataLimitCheck: ViewDataLimitCheck
): SourceDataShape => {
  const { viewSettings, viewInterface, scopeDiffState } = state;

  const {
    context,
    components,
    scenarioRelatedComponentIds,
    graphModel,
    scenario,
    loadedGraph,
    i18nLocale,
  } = payload;
  const [loadedScenarioData, scenarioRelatedComponents, { showScopeRelated }] =
    scenario;

  const performanceTrackingId = startViewLoadTracking(
    viewIdToUserFriendlyName[ViewIds.BLOCK_DIAGRAM],
    DEFAULT_EXPECTED_DURATION
  );

  const { rawGraph, nodeLabelsData, dataLimitExceeded, ...viewModel } =
    buildViewModel({
      viewInterface,
      context,
      components,
      scenarioRelatedComponentIds,
      viewSettings,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      isInScopeDiffMode: Boolean(scopeDiffState.scopeDiff),
      scopeDiff: scopeDiffState.scopeDiff,
      expandedStateChangingGroupId: null,
      showScopeRelated,
      getDistinctReferenceKey: legacyGetDistinctReferenceKey,
      shouldCollapseGraph: true,
      viewId: VIEW_ID,
      i18nLocale,
      dataLimitCheck,
    });

  return {
    ...state,
    dataLimitCheck,
    dataLimitExceeded,
    viewModel: {
      ...viewModel,
      nodeLabelsData,
    },
    streamState: {
      context,
      components,
      scenarioRelatedComponentIds,
      graphModel,
      loadedScenarioData,
      scenarioRelatedComponents,
      showScopeRelated,
      skipRender: false,
      isUpdatingAppState: false,
      rawGraph,
      getDistinctReferenceKey: legacyGetDistinctReferenceKey,
      performanceTrackingId,
    },
    noContext: false,
    loadedGraph,
    viewId: VIEW_ID,
  };
};

type ModelAndRenderingDataToPropsArgs = [
  SourceDataShape,
  SharedBlockDiagramsRenderingDataSource,
  I18nLocale,
  DiffMode,
  APIOrganizationUser[],
];
export const modelAndRenderingDataToProps = ([
  sourceData,
  { clearEdges, isContextChange, scenarioName },
  i18nLocale,
  activeDiffMode,
  users,
]: ModelAndRenderingDataToPropsArgs): BlockDiagramViewProperties => {
  const isViewpointMode = false;
  return {
    ...sourceData,
    hasUserBypassedDataLimit: sourceData.dataLimitCheck.hasUserBypassed,
    zoomComponentId: isContextChange
      ? sourceData.streamState.context.componentId
      : null,
    clearEdges,
    scenarioName,
    viewInstanceId: sourceData.viewInstanceId,
    isViewpointMode,
    isViewEmpty: !getGraphNodeCount(sourceData.viewModel),
    performanceTrackingId: sourceData.streamState.performanceTrackingId,
    i18nLocale,
    errorInfoBoxLabels: getErrorInfoBoxLabels(i18nLocale),
    legendProps: getGraphComponentLegendProps({
      componentMap: sourceData.streamState.rawGraph.componentMap,
      groupMap: sourceData.streamState.rawGraph.groupMap,
      edges: sourceData.streamState.rawGraph.referenceList,
      graphInterface: sourceData.viewInterface,
      formattingFilters: filterInterface.getFormattingFilters(),
      scopeDiff: sourceData.scopeDiffState.scopeDiff,
      displayShapesInsteadOfIcons: true,
      activeDiffMode,
      i18nLocale,
      users,
      isNewConditionalFormatting: false,
    }),
  };
};

type ViewContext = {
  viewSettings: BlockDiagramViewSettings;
  scopeDiffState: ScopeDiffState;
};

/**
 * Master reducer that handles ALL view model state changes.
 * Multiple scattered reducers of filtered streams with the same source tend to introduce
 * race conditions and / or duplicate updates, especially when we need to modify state.
 *
 * This reducer implements a single decision tree for the state modifications.
 * Filtered streams are only used to trigger events, while the unfiltered view context is
 * always passed in fresh from the store via withLatestFrom.
 */
const unifiedViewModelReducer = (
  previousState: SourceDataShape,
  event: ViewModelEvent,
  viewContext: ViewContext
): SourceDataShape => {
  const state = {
    ...previousState,
    ...viewContext,
    lastViewModelEvent: event,
  };

  switch (event.type) {
    // Full view model rebuild
    case ViewModelEventType.SOURCE_DATA_RESET: {
      return resetViewModel(state, event.payload, event.dataLimitCheck);
    }

    case ViewModelEventType.COMPONENT_DATA_UPDATED: {
      return handleComponentsUpdate(state, event.payload);
    }

    // Animation-only (no rebuild)
    case ViewModelEventType.SEPARATE_REFERENCES_CHANGED: {
      return {
        ...state,
        viewModel: {
          ...state.viewModel,
          ...cloneGraphItemsAsUpdate(state.viewModel),
        },
      };
    }

    // Incremental rebuilds with smart animations
    case ViewModelEventType.GROUP_TOGGLED: {
      return toggleCollapseGroupReducer(state, event.payload);
    }

    case ViewModelEventType.ALL_GROUPS_EXPANDED: {
      return expandAllGroupsReducer(state, event.payload);
    }

    case ViewModelEventType.ALL_GROUPS_COLLAPSED: {
      return collapseAllGroupsReducer(state, event.payload);
    }

    // UI-only view settings update (no view model changes)
    case ViewModelEventType.UI_SETTINGS_CHANGED: {
      return state;
    }

    default: {
      const _exhaustive: never = event;
      return _exhaustive;
    }
  }
};

export const getEmptySourceDataState = (
  viewInstanceId: string
): SourceDataShape => ({
  ...getDefaultRelationshipDiagramStateForWorkspaceMode(VIEW_ID),
  lastViewModelEvent: null,
  viewInterface: getDefaultRelationshipDiagramsViewInterface(),
  dataLimitCheck: DATA_LIMIT_CHECK_DISABLED,
  dataLimitExceeded: null,
  viewInstanceId,
});

export const getViewModel$ = ({
  viewInstanceId,
}: HasViewInstanceId): Observable<BlockDiagramViewProperties> => {
  const viewSettingsWithChanges$ = getWorkspaceModeViewSettingsWithChanges$(
    VIEW_ID,
    viewInstanceId
  );
  const sourceDataSettingsChangeEvent$ = getFilteredViewSettings$(
    viewSettingsWithChanges$,
    IGNORE_FOR_SOURCE_DATA_REBUILD
  );

  // #region view model events

  const hasBypassedDataLimit$: Observable<boolean> = action$.pipe(
    withNamespace(viewInstanceId),
    ofType(relationshipDiagramBypassDataLimit),
    map(returnTrue),
    startWith(false)
  );

  const sourceDataResetEvents$: Observable<GraphRebuildEvent> = combineLatest([
    globalViewModelResetSource$,
    sourceDataSettingsChangeEvent$, // we neglect the payload, this should only trigger the event
    hasBypassedDataLimit$,
  ]).pipe(
    map(([globalAppState, , hasUserBypassed]): GraphRebuildEvent => ({
      type: ViewModelEventType.SOURCE_DATA_RESET,
      payload: { ...globalAppState },
      dataLimitCheck: buildViewDataLimitCheck(hasUserBypassed),
    })),
    tap(() => {
      if (viewInstanceId) {
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
      }
    })
  );

  const componentUpdateEvents$: Observable<ComponentsUpdateEvent> =
    componentsChanged$.pipe(
      map((payload): ComponentsUpdateEvent => ({
        type: ViewModelEventType.COMPONENT_DATA_UPDATED,
        payload,
      }))
    );

  const separateReferencesEvents$: Observable<AnimationOnlyEvent> =
    viewSettingsWithChanges$.pipe(
      filter(isSeparateReferencesChange),
      map((payload): AnimationOnlyEvent => ({
        type: ViewModelEventType.SEPARATE_REFERENCES_CHANGED,
        payload,
      }))
    );

  const groupToggledEvents$: Observable<GroupCollapseEvent> = action$.pipe(
    withNamespace(viewInstanceId),
    ofType(relationshipDiagramToggleCollapseGroup),
    extractPayload(),
    map((payload): GroupCollapseEvent => ({
      type: ViewModelEventType.GROUP_TOGGLED,
      payload,
    }))
  );

  const allGroupsExpandedEvents$: Observable<GroupCollapseEvent> = action$.pipe(
    withNamespace(viewInstanceId),
    ofType(relationshipDiagramExpandAllGroups),
    extractPayload(),
    map((payload): GroupCollapseEvent => ({
      type: ViewModelEventType.ALL_GROUPS_EXPANDED,
      payload,
    })),
    tap(() => {
      if (viewInstanceId) {
        dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
      }
    })
  );

  const allGroupsCollapsedEvents$: Observable<GroupCollapseEvent> =
    action$.pipe(
      withNamespace(viewInstanceId),
      ofType(relationshipDiagramCollapseAllGroups),
      extractPayload(),
      map((payload): GroupCollapseEvent => ({
        type: ViewModelEventType.ALL_GROUPS_COLLAPSED,
        payload,
      })),
      tap(() => {
        if (viewInstanceId) {
          dispatchAction(notifyViewLoading(viewInstanceId, { isBusy: true }));
        }
      })
    );

  // settings that should only appear in props and not change the view model
  const uiOnlySettingsEvents$: Observable<UiOnlyEvent> =
    viewSettingsWithChanges$.pipe(
      filter(
        settings =>
          settings.changedSettings.length > 0 &&
          settings.changedSettings.every(([changedKey]) =>
            UI_ONLY_SETTINGS.includes(changedKey)
          )
      ),
      map((payload): UiOnlyEvent => ({
        type: ViewModelEventType.UI_SETTINGS_CHANGED,
        payload,
      }))
    );

  // #endregion view model events

  /**
   * All view model state changes flow through a single reducer with fresh settings from store.
   */
  const viewModel$: Observable<SourceDataShape> = merge(
    sourceDataResetEvents$,
    componentUpdateEvents$,
    separateReferencesEvents$,
    uiOnlySettingsEvents$,
    groupToggledEvents$,
    allGroupsExpandedEvents$,
    allGroupsCollapsedEvents$
  ).pipe(
    withLatestFrom(
      viewSettingsWithChanges$.pipe(
        map(({ currentSettings }) => currentSettings)
      ),
      scopeDiff$
    ),
    scan(
      (state, [event, viewSettings, scopeDiffState]) =>
        unifiedViewModelReducer(state, event, {
          viewSettings,
          scopeDiffState,
        }),
      getEmptySourceDataState(viewInstanceId)
    ),
    tap(({ viewModel, lastViewModelEvent }) => {
      const modelEventType = lastViewModelEvent?.type;

      if (
        !modelEventType ||
        modelEventType === ViewModelEventType.UI_SETTINGS_CHANGED ||
        modelEventType === ViewModelEventType.SEPARATE_REFERENCES_CHANGED
      ) {
        return;
      }

      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return combineLatest([
    viewModel$,
    sharedBlockDiagramsRenderingDataSource$,
    i18nLocale$,
    activeDiffMode$,
    allActiveOrgUsers$,
  ]).pipe(
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    map(modelAndRenderingDataToProps),
    tap(({ viewModel, isViewEmpty, performanceTrackingId, viewInterface }) => {
      if (performanceTrackingId) {
        recordViewModelReady(performanceTrackingId, {
          ...getPerformanceTrackingData(
            viewModel,
            viewInterface.getComponentWorkspaceId
          ),
        });
      }

      dispatchAction(
        updateViewEmptyStateData({
          viewEmptyStateData: { isViewEmpty },
          viewId: VIEW_ID,
        })
      );
    })
  );
};
